/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  useCallback,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { View, Text, Platform, useWindowDimensions } from 'react-native';
import MasonryGrid from './MasonryGrid';
import RecommendationCard from './RecommendationCard';
import SpinnerLoaderView from '@airasia-phoenix/react-native/components/SpinnerLoaderView';
import {
  SpinnerLoaderViewStyle,
  SpinnerLoaderViewSize,
} from '@airasia-phoenix/react-native/components/SpinnerLoaderView/types';
import ButtonView from '@airasia-phoenix/react-native/components/ButtonView';
import { ButtonViewType } from '@airasia-phoenix/react-native/components/ButtonView';
import { useTranslation } from 'react-i18next';
import { useLogger } from '@airasia-rn/core-logger';
import { styles } from './Recommend.styles';
import { usePromotions } from '../../hooks/usePromotions';
import {
  RecommendationEvents,
  RECOMMENDATION_TAB_TITLE_BY_ID,
  trackRecommendationApiFailure,
  trackRecommendationImpression,
  TRACKING_PAGE_SIZE,
  QUARTILE_PAGE_SIZE,
  trackPromotionWidgetViewed,
  trackPromotionListingViewBatch,
  trackPromotionListingClick,
  VIEW_ITEM_PAGE_SIZE,
  type RecommendationTrackingItem,
} from '../../analytics/recommendation';
import mapRecommendationToCardProps from '../../utils/mapRecommendationToCardProps';
import type { RecommendationApiItem } from '../../models/recommendTabResponse';

export interface PromotionFeedRef {
  maybeLoadMoreOnScroll: () => void;
}

const TAB_NAME_PROMOTIONS = RECOMMENDATION_TAB_TITLE_BY_ID.promotions;

const PromotionFeed = forwardRef<
  PromotionFeedRef,
  {
    tabName?: string;
    onRecommendationPress?: (params: {
      itemId?: string;
      lob?: string;
      title?: string;
      deepLink?: string;
    }) => void;
    onPressCta?: (params: { deepLink?: string }) => void;
  }
>(({ tabName = 'promotions', onRecommendationPress, onPressCta }, ref) => {
  const { t } = useTranslation();
  const { logEvent, logError } = useLogger();
  const {
    items,
    loading,
    loadingMore,
    loadMoreError,
    error,
    errorInfo,
    loadMore,
    hasMore,
    retry,
    refreshing,
    refresh,
  } = usePromotions();

  const lastApiErrorLabelRef = useRef<string>('');
  const impressedIdsRef = useRef<Set<string>>(new Set());
  const endReachedSentinelRef = useRef<View>(null);
  const isCheckingEndReachedRef = useRef(false);
  const autoLoadInFlightRef = useRef(false);
  const previousLoadMoreErrorRef = useRef(loadMoreError);
  const { height: windowHeight } = useWindowDimensions();
  const trackedViewIndexRef = useRef(0);
  const viewItemLastIndexRef = useRef(0);
  const hasTrackedInitialBatchRef = useRef(false);
  const hasTrackedWidgetViewedRef = useRef(false);

  useEffect(() => {
    if (refreshing || (previousLoadMoreErrorRef.current && !loadMoreError)) {
      autoLoadInFlightRef.current = false;
    }

    previousLoadMoreErrorRef.current = loadMoreError;
  }, [loadMoreError, refreshing]);

  const trackViewItemBatch = useCallback(
    (itemsToTrack: RecommendationApiItem[]) => {
      let start = viewItemLastIndexRef.current;
      while (itemsToTrack.length - start >= VIEW_ITEM_PAGE_SIZE) {
        const batch = itemsToTrack.slice(start, start + VIEW_ITEM_PAGE_SIZE);
        logEvent(
          RecommendationEvents.View_Item_List,
          trackPromotionListingViewBatch(batch, start),
        );
        start += VIEW_ITEM_PAGE_SIZE;
      }

      if (start < itemsToTrack.length) {
        logEvent(
          RecommendationEvents.View_Item_List,
          trackPromotionListingViewBatch(itemsToTrack.slice(start), start),
        );
        start = itemsToTrack.length;
      }
      viewItemLastIndexRef.current = start;
    },
    [logEvent],
  );
  const maybeLoadMoreOnScroll = useCallback(() => {
    // Fire view_item_list for the initial batch the first time tab is active.
    if (!hasTrackedInitialBatchRef.current && items.length > 0) {
      hasTrackedInitialBatchRef.current = true;
      trackViewItemBatch(items);
    }

    // Fire aa_home_promowidget_viewed with quartile 0 on first load.
    if (!hasTrackedWidgetViewedRef.current && items.length > 0) {
      hasTrackedWidgetViewedRef.current = true;
      logEvent(
        RecommendationEvents.Home_Promotion_Widget_Viewed,
        trackPromotionWidgetViewed(
          TAB_NAME_PROMOTIONS,
          0, // quartile 0 = first load, per spec
          items.length,
        ),
      );
    }

    if (
      Platform.OS === 'web' ||
      loading ||
      loadingMore ||
      !hasMore ||
      loadMoreError ||
      (error && items.length > 0) ||
      autoLoadInFlightRef.current ||
      isCheckingEndReachedRef.current
    ) {
      return;
    }

    const sentinel = endReachedSentinelRef.current;
    if (!sentinel) {
      return;
    }

    isCheckingEndReachedRef.current = true;
    sentinel.measureInWindow((_x, y) => {
      isCheckingEndReachedRef.current = false;

      const threshold = 220;
      const isNearViewportBottom =
        y <= windowHeight + threshold && y >= -threshold;

      if (isNearViewportBottom) {
        autoLoadInFlightRef.current = true;
        void Promise.resolve(loadMore()).finally(() => {
          autoLoadInFlightRef.current = false;
        });
      }
    });
  }, [
    loading,
    loadingMore,
    hasMore,
    loadMoreError,
    error,
    items,
    windowHeight,
    loadMore,
    logEvent,
    trackViewItemBatch,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      maybeLoadMoreOnScroll,
    }),
    [maybeLoadMoreOnScroll],
  );

  const retryInitial = useCallback(() => {
    retry();
  }, [retry]);

  const retryPagination = useCallback(() => {
    void loadMore();
  }, [loadMore]);

  const handleCardPress = useCallback(
    (item: RecommendationApiItem) => {
      const position =
        items.findIndex((row) => row.lobItemId === item.lobItemId) + 1;
      if (position > 0) {
        logEvent(
          RecommendationEvents.aa_home_promowidget_clicked,
          trackPromotionListingClick(item, position),
        );
      }
    },
    [items, logEvent],
  );

  useEffect(() => {
    if (!error) return;
    const code = errorInfo?.code;
    const message = errorInfo?.message ?? 'Unknown error';
    const dedupeKey = `${tabName}|${code}|${message}`;
    if (lastApiErrorLabelRef.current === dedupeKey) return;
    lastApiErrorLabelRef.current = dedupeKey;
    logError(
      RecommendationEvents.Api_Failure,
      trackRecommendationApiFailure(TAB_NAME_PROMOTIONS, code, message),
    );
  }, [error, errorInfo, tabName, logError]);

  // Fires aa_home_promowidget_viewed on scroll quartiles as pagination loads
  useEffect(() => {
    if (!items.length) return;
    const currentMax = items.length;
    let i = trackedViewIndexRef.current + 1;
    while (i <= currentMax) {
      if (i % TRACKING_PAGE_SIZE === 0) {
        logEvent(
          RecommendationEvents.Home_Promotion_Widget_Viewed,
          trackPromotionWidgetViewed(
            TAB_NAME_PROMOTIONS,
            Math.round((i * 100) / QUARTILE_PAGE_SIZE),
            i,
          ),
        );
      }
      i += 1;
    }
    trackedViewIndexRef.current = currentMax;
  }, [items.length, logEvent]);

  // Fires view_item_list for newly paginated items beyond the initial batch.
  useEffect(() => {
    trackViewItemBatch(items);
  }, [items, trackViewItemBatch]);

  const onViewableItemsChanged = useCallback(
    ({
      viewableItems,
    }: {
      viewableItems: {
        item?: RecommendationTrackingItem;
        isViewable?: boolean;
      }[];
    }) => {
      viewableItems.forEach((entry) => {
        if (!entry.isViewable || !entry.item) return;
        const id = entry.item?.lobItemId ?? entry.item?.itemId;
        if (!id || impressedIdsRef.current.has(id)) return;
        impressedIdsRef.current.add(id);
        logEvent(
          RecommendationEvents.Impression,
          trackRecommendationImpression(entry.item),
        );
      });
    },
    [logEvent],
  );

  if (loading && items.length === 0)
    return (
      <View style={styles.initialStateContainer}>
        <View style={styles.initialStateContent}>
          <SpinnerLoaderView
            spinnerStyle={SpinnerLoaderViewStyle.SPINNER}
            size={SpinnerLoaderViewSize.LARGE}
          />
        </View>
      </View>
    );

  if (error && items.length === 0)
    return (
      <View style={styles.initialStateContainer}>
        <View style={styles.initialStateContent}>
          <Text style={styles.initialStateMessage}>
            {t('homePage.recommendationsError')}
          </Text>

          <View style={styles.initialStateActionContainer}>
            <ButtonView
              text={t('homePage.retry')}
              type={ButtonViewType.SECONDARY_SMALL}
              onPress={retryInitial}
              disabled={false}
            />
          </View>
        </View>
      </View>
    );

  const handleLoadMore = () => {
    if (loading || loadingMore || !hasMore) return;
    loadMore();
  };

  const renderFooter = () => {
    if (loadingMore)
      return (
        <View style={styles.feedLoaderContainer}>
          <SpinnerLoaderView
            spinnerStyle={SpinnerLoaderViewStyle.SPINNER}
            size={SpinnerLoaderViewSize.SMALL}
          />
        </View>
      );

    if (loadMoreError)
      return (
        <View style={styles.feedLoaderContainer}>
          <Text style={styles.initialStateMessage}>
            {t('homePage.recommendationsError')}
          </Text>
          <View style={styles.initialStateActionContainer}>
            <ButtonView
              text={t('homePage.retry')}
              type={ButtonViewType.SECONDARY_SMALL}
              onPress={retryPagination}
              disabled={false}
            />
          </View>
        </View>
      );

    if (Platform.OS === 'web' && hasMore)
      return (
        <View style={styles.feedLoaderContainer}>
          <ButtonView
            text={t('homePage.seeMore')}
            type={ButtonViewType.SECONDARY_SMALL}
            onPress={() => void loadMore()}
            disabled={false}
          />
        </View>
      );

    return null;
  };

  const renderableItems = items.filter((item) =>
    Boolean(mapRecommendationToCardProps(item, onRecommendationPress)),
  );

  if (!loading && !error && renderableItems.length === 0)
    return (
      <View style={styles.emptyStateContainer}>
        <Text style={styles.initialStateMessage}>
          {t('homePage.noPromotions')}
        </Text>
      </View>
    );

  return (
    <View style={styles.feedContainer}>
      <MasonryGrid
        data={renderableItems}
        renderItem={(item) => (
          <RecommendationCard
            item={item}
            onTrackClick={handleCardPress}
            onNavigate={onRecommendationPress}
            onPressCta={onPressCta}
          />
        )}
        onEndReached={handleLoadMore}
        ListFooterComponent={
          <View>
            {renderFooter()}
            <View
              ref={endReachedSentinelRef}
              style={styles.endReachedSentinel}
            />
          </View>
        }
        refreshing={refreshing}
        onRefresh={refresh}
        onViewableItemsChanged={onViewableItemsChanged}
      />
    </View>
  );
});

PromotionFeed.displayName = 'PromotionFeed';
export default PromotionFeed;
